import { PrintingToolConfiguration } from '@innovamat/smart-guides';

import styled from '@emotion/styled';
import { useRef, useState } from 'react';

import { Button, Loader, ToggleButton } from '@innovamat/glimmer-components';
import { usePrint } from '@innovamat/hooks';

import { usePrintingToolStore } from '@innovamat/smart-guides';
import { Navigate } from 'react-router-domv6';
import { moveContentToPrint } from '../print-guides/move-content-to-print';
import { PrintGuide } from '../print-guides/print-guide';
import { usePrintingToolAuthorization } from './hooks/use-printing-tool-authorization';

const Wrapper = styled.div`
  padding: 24px;
  width: 100%;
  height: var(--realVh);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding-bottom: 58px;

  @page {
    margin: 12.5mm;
  }
`;

const PrintSection = styled.div`
  background-color: white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  border-top: 1px solid #e5e5e5;
  padding: 8px;
  width: 280px;
  position: fixed;
  bottom: 10px;
  gap: 4px;
  left: 50%;
  border-radius: 8px;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
`;

const Disclaimer = styled.div<{ show: boolean }>`
  font-size: 12px;
  color: #888;

  max-height: ${({ show }) => (show ? '100px' : '0px')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  align-items: center;
`;

type Props = {
  onPrint: () => void;
  loading: boolean;
};

function PrintFooter({ onPrint, loading }: Props) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <PrintSection
      onMouseEnter={() => setShowDisclaimer(true)}
      onMouseLeave={() => setShowDisclaimer(false)}
    >
      <>
        <Button fill onClick={onPrint} variant="accent" loading={loading}>
          Imprimir
        </Button>
        <Disclaimer show={showDisclaimer}>
          Recuerda modificar las opciones para los márgenes y la leyenda a la
          hora de imprimir
        </Disclaimer>
      </>
    </PrintSection>
  );
}

const LegendContainer = styled.div`
  position: fixed;
  bottom: 10px;
  left: 10px;
  background-color: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
`;

function Legend({
  isEditMode,
  setIsEditMode,
}: {
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}) {
  return (
    <LegendContainer>
      <LegendTitle>Manual Mode</LegendTitle>
      <ToggleButton
        checked={isEditMode}
        onChange={() => setIsEditMode(!isEditMode)}
      />
    </LegendContainer>
  );
}

function PrintingTool(): JSX.Element {
  const { componentRef, onPrint, loading } = usePrint('Smart Guides');
  const { configuration } = usePrintingToolStore();
  const { shouldRedirect, isLoading } = usePrintingToolAuthorization();

  const idsArray = configuration.guideIds;
  const language = configuration.language;
  const region = configuration.region;
  const standard = configuration.standard;
  const courseOrder = configuration.courseOrder;

  const [isEditMode, setIsEditMode] = useState(false);
  const isContentMoved = useRef(false);

  const handlePrint = (): void => {
    if (!configuration.autoBreakPage || isContentMoved.current) {
      onPrint();
    } else {
      moveContentToPrint();
      isContentMoved.current = true;
      setTimeout(() => {
        onPrint();
      }, 500);
    }
  };

  if (isLoading) {
    return (
      <LoaderContainer>
        <Loader size="md" />
      </LoaderContainer>
    );
  }

  if (shouldRedirect) {
    return <Navigate to="/" />;
  }

  return (
    <div>
      <PrintingToolConfiguration />
      <Wrapper>
        <PrintFooter onPrint={handlePrint} loading={loading} />
        {!configuration.autoBreakPage && (
          <Legend isEditMode={isEditMode} setIsEditMode={setIsEditMode} />
        )}

        <div ref={componentRef}>
          {idsArray?.map((id: string) => (
            <PrintGuide
              id={id}
              hasBreakPage={true}
              isEditMode={isEditMode}
              language={language}
              region={region}
              standard={standard}
              configuration={configuration}
              courseOrder={courseOrder}
            />
          ))}
        </div>
      </Wrapper>
    </div>
  );
}

export { PrintingTool };
